import { useState, useEffect } from 'react'
import { Star, Play, Plus, Check, Calendar } from 'lucide-react'

interface TVSeriesWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Series {
  id: number
  name: string
  poster: string
  backdrop: string
  overview: string
  rating: number
  nextEpisode?: {
    season: number
    episode: number
    title: string
    airDate: string
  }
  status: 'watching' | 'completed' | 'plan_to_watch'
  progress?: {
    season: number
    episode: number
  }
}

export default function TVSeriesWidget({ widget, isExpanded }: TVSeriesWidgetProps) {
  const [series, setSeries] = useState<Series[]>([])
  const [filter, setFilter] = useState<'all' | 'watching' | 'completed' | 'plan_to_watch'>('all')
  const [selectedSeries, setSelectedSeries] = useState<Series | null>(null)

  // Mock data - Replace with TMDB API integration
  useEffect(() => {
    const mockSeries: Series[] = [
      {
        id: 1,
        name: 'Breaking Bad',
        poster: 'https://image.tmdb.org/t/p/w500/ggFHVNu6YYI5L9pCfOacjizRGt.jpg',
        backdrop: 'https://image.tmdb.org/t/p/w1280/tsRy63Mu5cu8etL1X7ZLyf7UP1M.jpg',
        overview: 'A high school chemistry teacher turned meth producer.',
        rating: 9.5,
        status: 'watching',
        progress: {
          season: 3,
          episode: 7
        },
        nextEpisode: {
          season: 3,
          episode: 8,
          title: 'I See You',
          airDate: '2024-01-20'
        }
      },
      {
        id: 2,
        name: 'The Last of Us',
        poster: 'https://image.tmdb.org/t/p/w500/uKvVjHNqB5VmOrdxqAt2F7J78ED.jpg',
        backdrop: 'https://image.tmdb.org/t/p/w1280/56v2KjBlU4XaOv9rVYEQypROD7P.jpg',
        overview: 'Twenty years after a fungal outbreak, survivors navigate a post-apocalyptic world.',
        rating: 8.9,
        status: 'watching',
        progress: {
          season: 1,
          episode: 5
        },
        nextEpisode: {
          season: 1,
          episode: 6,
          title: 'Kin',
          airDate: '2024-01-22'
        }
      },
      {
        id: 3,
        name: 'Game of Thrones',
        poster: 'https://image.tmdb.org/t/p/w500/7WUHnWGx5OO145IRxPDUkQSh4C7.jpg',
        backdrop: 'https://image.tmdb.org/t/p/w1280/suopoADq0k8YZr4dQXcU6pToj6s.jpg',
        overview: 'Nine noble families fight for control of the Iron Throne.',
        rating: 9.2,
        status: 'completed',
        progress: {
          season: 8,
          episode: 6
        }
      },
      {
        id: 4,
        name: 'Stranger Things',
        poster: 'https://image.tmdb.org/t/p/w500/49WJfeN0moxb9IPfGn8AIqMGskD.jpg',
        backdrop: 'https://image.tmdb.org/t/p/w1280/56v2KjBlU4XaOv9rVYEQypROD7P.jpg',
        overview: 'A group of kids uncover supernatural forces in their town.',
        rating: 8.7,
        status: 'plan_to_watch'
      }
    ]

    setSeries(mockSeries)
  }, [])

  const filteredSeries = filter === 'all' 
    ? series 
    : series.filter(s => s.status === filter)

  const handleMarkEpisodeWatched = (seriesId: number) => {
    setSeries(series.map(s => {
      if (s.id === seriesId && s.progress) {
        return {
          ...s,
          progress: {
            season: s.progress.season,
            episode: s.progress.episode + 1
          }
        }
      }
      return s
    }))
  }

  const handleStatusChange = (seriesId: number, status: Series['status']) => {
    setSeries(series.map(s => 
      s.id === seriesId ? { ...s, status } : s
    ))
  }

  const renderCompactView = () => (
    <div className="space-y-3">
      {filteredSeries.slice(0, 3).map((show) => (
        <div
          key={show.id}
          className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition cursor-pointer"
          onClick={() => isExpanded && setSelectedSeries(show)}
        >
          <img
            src={show.poster}
            alt={show.name}
            className="w-12 h-16 object-cover rounded shadow"
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 text-sm truncate">{show.name}</div>
            {show.progress && (
              <div className="text-xs text-gray-600">
                S{show.progress.season}E{show.progress.episode}
              </div>
            )}
            <div className="flex items-center gap-1 mt-1">
              <Star size={12} className="text-yellow-500 fill-current" />
              <span className="text-xs text-gray-600">{show.rating}</span>
            </div>
          </div>
          {show.status === 'watching' && show.nextEpisode && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleMarkEpisodeWatched(show.id)
              }}
              className="p-1 hover:bg-green-100 rounded transition"
              title="Mark episode watched"
            >
              <Check size={16} className="text-green-600" />
            </button>
          )}
        </div>
      ))}
    </div>
  )

  const renderExpandedView = () => (
    <div className="h-full flex gap-4">
      {/* Series List */}
      <div className="w-1/3 overflow-y-auto pr-2">
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {(['all', 'watching', 'completed', 'plan_to_watch'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-sm whitespace-nowrap transition ${
                filter === f
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? 'All' : f.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {filteredSeries.map((show) => (
            <button
              key={show.id}
              onClick={() => setSelectedSeries(show)}
              className={`w-full flex items-start gap-3 p-3 rounded-lg transition text-left ${
                selectedSeries?.id === show.id
                  ? 'bg-primary-50 border-2 border-primary-500'
                  : 'hover:bg-gray-50 border-2 border-transparent'
              }`}
            >
              <img
                src={show.poster}
                alt={show.name}
                className="w-16 h-20 object-cover rounded shadow"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm truncate">{show.name}</div>
                {show.progress && (
                  <div className="text-xs text-gray-600 mt-1">
                    S{show.progress.season}E{show.progress.episode}
                  </div>
                )}
                <div className="flex items-center gap-1 mt-1">
                  <Star size={12} className="text-yellow-500 fill-current" />
                  <span className="text-xs text-gray-600">{show.rating}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Series Detail */}
      <div className="flex-1 overflow-y-auto">
        {selectedSeries ? (
          <div>
            {/* Backdrop */}
            <div className="relative mb-4 rounded-lg overflow-hidden">
              <img
                src={selectedSeries.backdrop}
                alt={selectedSeries.name}
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-2xl font-bold text-white mb-2">{selectedSeries.name}</h2>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <Star size={16} className="text-yellow-500 fill-current" />
                    <span className="text-white font-semibold">{selectedSeries.rating}</span>
                  </div>
                  <span className="px-2 py-1 bg-white/20 backdrop-blur text-white text-xs rounded">
                    {selectedSeries.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Overview */}
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Overview</h3>
              <p className="text-gray-600 text-sm">{selectedSeries.overview}</p>
            </div>

            {/* Progress */}
            {selectedSeries.progress && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Your Progress</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-700">
                      Season {selectedSeries.progress.season}, Episode {selectedSeries.progress.episode}
                    </span>
                    <button
                      onClick={() => handleMarkEpisodeWatched(selectedSeries.id)}
                      className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition"
                    >
                      <Check size={14} />
                      Mark Watched
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Next Episode */}
            {selectedSeries.nextEpisode && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Next Episode</h3>
                <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        S{selectedSeries.nextEpisode.season}E{selectedSeries.nextEpisode.episode}: {selectedSeries.nextEpisode.title}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
                        <Calendar size={14} />
                        {new Date(selectedSeries.nextEpisode.airDate).toLocaleDateString()}
                      </div>
                    </div>
                    <button className="p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition">
                      <Play size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Status Change */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Change Status</h3>
              <div className="flex gap-2">
                {(['watching', 'completed', 'plan_to_watch'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(selectedSeries.id, status)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm transition ${
                      selectedSeries.status === status
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Select a series to view details
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden">
        {isExpanded ? renderExpandedView() : renderCompactView()}
      </div>

      {!isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition">
            <Plus size={16} />
            Add Series
          </button>
        </div>
      )}
    </div>
  )
}




